import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product } from '../types/index.ts';
import { cartAPI } from '../services/api.ts';
import { useAuth } from './AuthContext.tsx';

interface CartItem {
  id: number;
  userId: number;
  productId: number;
  quantity: number;
  product: Product;
  createdAt: string;
  updatedAt: string;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  totalDeposit: number;
  grandTotal: number;
  loading: boolean;
  error: string | null;
}

interface CartContextType extends CartState {
  addToCart: (product: Product, quantity?: number) => Promise<void>;
  removeFromCart: (productId: number) => Promise<void>;
  updateQuantity: (productId: number, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  syncCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Helper function to get localStorage cart
const getLocalStorageCart = (): Array<{ productId: number; quantity: number }> => {
  try {
    const cart = localStorage.getItem('h2go_cart');
    return cart ? JSON.parse(cart) : [];
  } catch {
    return [];
  }
};

// Helper function to save localStorage cart
const saveLocalStorageCart = (cartItems: Array<{ productId: number; quantity: number }>) => {
  try {
    localStorage.setItem('h2go_cart', JSON.stringify(cartItems));
  } catch {
    // Ignore localStorage errors
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, isAdmin } = useAuth();
  const [state, setState] = useState<CartState>({
    items: [],
    totalItems: 0,
    totalPrice: 0,
    totalDeposit: 0,
    grandTotal: 0,
    loading: false,
    error: null,
  });

  // Load cart from database when customer user logs in (not for admin users)
  useEffect(() => {
    if (isAuthenticated && user && !isAdmin) {
      loadCartFromDatabase();
    } else {
      // Clear cart when user logs out or is admin
      setState(prev => ({
        ...prev,
        items: [],
        totalItems: 0,
        totalPrice: 0,
        totalDeposit: 0,
        grandTotal: 0,
        error: null,
      }));
    }
  }, [isAuthenticated, user, isAdmin]);

  const loadCartFromDatabase = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await cartAPI.getCart();
      const cartData = response.data.data;
      
      setState({
        items: cartData.items,
        totalItems: cartData.totalItems,
        totalPrice: cartData.totalPrice,
        totalDeposit: cartData.totalDeposit,
        grandTotal: cartData.grandTotal,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error loading cart:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to load cart',
      }));
    }
  };

  const syncCart = async () => {
    if (!isAuthenticated || isAdmin) return;

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const localCartItems = getLocalStorageCart();
      if (localCartItems.length === 0) {
        await loadCartFromDatabase();
        return;
      }

      const response = await cartAPI.syncCart(localCartItems);
      const cartData = response.data.data;
      
      setState({
        items: cartData.items,
        totalItems: cartData.totalItems,
        totalPrice: cartData.totalPrice,
        totalDeposit: cartData.totalDeposit,
        grandTotal: cartData.grandTotal,
        loading: false,
        error: null,
      });

      // Clear localStorage cart after successful sync
      saveLocalStorageCart([]);
    } catch (error: any) {
      console.error('Error syncing cart:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to sync cart',
      }));
    }
  };

  const addToCart = async (product: Product, quantity: number = 1) => {
    if (!isAuthenticated || isAdmin) {
      // Store in localStorage for non-authenticated users
      const localCartItems = getLocalStorageCart();
      const existingItemIndex = localCartItems.findIndex(item => item.productId === product.id);
      
      if (existingItemIndex >= 0) {
        localCartItems[existingItemIndex].quantity += quantity;
      } else {
        localCartItems.push({ productId: product.id, quantity });
      }
      
      saveLocalStorageCart(localCartItems);
      
      // Update local state
      const existingItem = state.items.find(item => item.productId === product.id);
      let newItems: CartItem[];
      
      if (existingItem) {
        newItems = state.items.map(item =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Create a temporary CartItem for localStorage
        const tempCartItem: CartItem = {
          id: Date.now(), // Temporary ID
          userId: 0,
          productId: product.id,
          quantity,
          product,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        newItems = [...state.items, tempCartItem];
      }
      
      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalPrice = newItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
      const totalDeposit = newItems.reduce((sum, item) => sum + ((item.product.deposit || 0) * item.quantity), 0);
      
      setState(prev => ({
        ...prev,
        items: newItems,
        totalItems,
        totalPrice,
        totalDeposit,
        grandTotal: totalPrice + totalDeposit,
      }));
      
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      await cartAPI.addToCart(product.id, quantity);
      await loadCartFromDatabase();
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to add item to cart',
      }));
      throw error;
    }
  };

  const removeFromCart = async (productId: number) => {
    if (!isAuthenticated || isAdmin) {
      // Remove from localStorage
      const localCartItems = getLocalStorageCart();
      const filteredItems = localCartItems.filter(item => item.productId !== productId);
      saveLocalStorageCart(filteredItems);
      
      // Update local state
      const newItems = state.items.filter(item => item.productId !== productId);
      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalPrice = newItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
      const totalDeposit = newItems.reduce((sum, item) => sum + ((item.product.deposit || 0) * item.quantity), 0);
      
      setState(prev => ({
        ...prev,
        items: newItems,
        totalItems,
        totalPrice,
        totalDeposit,
        grandTotal: totalPrice + totalDeposit,
      }));
      
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      await cartAPI.removeFromCart(productId);
      await loadCartFromDatabase();
    } catch (error: any) {
      console.error('Error removing from cart:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to remove item from cart',
      }));
      throw error;
    }
  };

  const updateQuantity = async (productId: number, quantity: number) => {
    if (quantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    if (!isAuthenticated || isAdmin) {
      // Update localStorage
      const localCartItems = getLocalStorageCart();
      const itemIndex = localCartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex >= 0) {
        localCartItems[itemIndex].quantity = quantity;
        saveLocalStorageCart(localCartItems);
      }
      
      // Update local state
      const newItems = state.items.map(item =>
        item.productId === productId
          ? { ...item, quantity }
          : item
      );
      
      const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalPrice = newItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
      const totalDeposit = newItems.reduce((sum, item) => sum + ((item.product.deposit || 0) * item.quantity), 0);
      
      setState(prev => ({
        ...prev,
        items: newItems,
        totalItems,
        totalPrice,
        totalDeposit,
        grandTotal: totalPrice + totalDeposit,
      }));
      
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      await cartAPI.updateCartItem(productId, quantity);
      await loadCartFromDatabase();
    } catch (error: any) {
      console.error('Error updating cart item:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to update cart item',
      }));
      throw error;
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated || isAdmin) {
      // Clear localStorage
      saveLocalStorageCart([]);
      
      // Clear local state
      setState(prev => ({
        ...prev,
        items: [],
        totalItems: 0,
        totalPrice: 0,
        totalDeposit: 0,
        grandTotal: 0,
      }));
      
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      await cartAPI.clearCart();
      setState(prev => ({
        ...prev,
        items: [],
        totalItems: 0,
        totalPrice: 0,
        totalDeposit: 0,
        grandTotal: 0,
        loading: false,
        error: null,
      }));
    } catch (error: any) {
      console.error('Error clearing cart:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to clear cart',
      }));
      throw error;
    }
  };

  const refreshCart = async () => {
    if (isAuthenticated && !isAdmin) {
      await loadCartFromDatabase();
    }
  };

  return (
    <CartContext.Provider value={{
      ...state,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      syncCart,
      refreshCart,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};